/**
 * Barre collante de remontée.
 *
 * Elle n'ajoute pas au panier : elle ramène à la zone de choix. Au dixième écran
 * de défilement, personne ne se souvient de ce qu'il a coché — le renvoyer au
 * choix vaut mieux qu'un ajout à l'aveugle.
 */
class StickyCta extends HTMLElement {
  connectedCallback() {
    this.target = document.querySelector(this.dataset.target || '');
    this.bar = this.querySelector('.sticky-cta__bar');
    this.button = this.querySelector('.sticky-cta__button');

    if (!this.target || !this.bar) {
      // Sans ancre, la barre n'a nulle part où ramener : on ne l'affiche pas.
      this.hidden = true;
      return;
    }

    // La barre déménage sur `body`.
    //
    // `position: fixed` ne se résout contre le viewport que si aucun ancêtre ne
    // porte `transform`, `filter`, `contain`, `content-visibility`,
    // `view-transition-name` ou une opacité inférieure à 1. Le premier qui en
    // porte un devient son bloc conteneur *et* son contexte d'empilement : la
    // barre est alors peinte à la place de cet ancêtre dans la pile, et son
    // propre z-index ne pèse plus que face à ses frères. Une section qui suit
    // lui passe devant quel que soit le chiffre qu'on lui donne — monter de 7 à
    // 10 n'avait rien changé, c'est la signature du symptôme.
    //
    // Plutôt que de chercher l'ancêtre fautif et de dépendre de lui, on sort la
    // barre de la section. Sur `body`, il n'y a plus d'ancêtre à piéger.
    document.body.appendChild(this.bar);

    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;
        this.scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.update();
      },
      { threshold: 0 }
    );

    // L'élément qui déclenche l'apparition n'est pas forcément celui où la
    // barre ramène. Depuis que la taille est passée en tête de la zone de
    // choix (2026-09-27), la barre ramène au sélecteur de taille ; observée
    // sur lui, elle apparaissait dès qu'il sortait de l'écran, les paliers et
    // le bouton d'achat encore visibles. Elle attend désormais que le bouton
    // d'achat soit dépassé (`data-watch`), et ramène toujours à la taille.
    const watched = (this.dataset.watch && document.querySelector(this.dataset.watch)) || this.target;
    this.observer.observe(watched);

    // La barre se replie sur la toute fin de la page.
    //
    // Elle est en `position: fixed` et rien ne réserve sa hauteur en bas du
    // document : arrivé au bout, elle recouvrait la dernière rangée du footer.
    // C'est en tablette et en mobile que ça se voyait, le footer y étant empilé
    // — la ligne de copyright passait dessous.
    //
    // Se replier plutôt que réserver la place : réserver aurait demandé de
    // mesurer la hauteur de la barre, de la reporter en variable CSS et de la
    // reprendre à chaque changement de largeur — et surtout de poser ce retrait
    // sur la dernière *section* du footer, la seule qui peigne son fond. Un
    // retrait sur le groupe aurait laissé une bande de page claire sous le
    // footer sombre.
    //
    // 🛑 L'ancre est la **dernière section** du pied de page, pas le pied de page
    // entier. Sur `<footer>`, la barre se repliait dès que son bord haut entrait
    // dans l'écran — soit, en mobile où le footer s'empile, un écran entier avant
    // la fin — et ne revenait qu'une fois tout le footer ressorti. Sur la
    // dernière section, le repli et le retour se jouent sur les quelques dizaines
    // de pixels où le recouvrement a effectivement lieu.
    //
    // La liste des sections plutôt que `lastElementChild` : selon la version,
    // Shopify enveloppe ou non un groupe de sections dans un conteneur. Chercher
    // les sections par leur identifiant donne la même ancre dans les deux cas.
    const footer = document.querySelector('footer');
    const sections = footer?.querySelectorAll('[id^="shopify-section"]');
    const anchor = sections?.[sections.length - 1] || footer;

    if (anchor) {
      this.footerObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          this.footerReached = entry.isIntersecting;
          this.update();
        },
        { threshold: 0 }
      );

      this.footerObserver.observe(anchor);
    }

    // La barre se replie aussi devant un CTA de la page qui fait déjà son
    // travail — le closer de fin de fiche. Deux boutons orange l'un sur
    // l'autre ne font plus un appel à l'action, et la charte n'en admet qu'un.
    const hideZones = document.querySelectorAll('[data-sticky-cta-hide]');

    if (hideZones.length) {
      this.visibleHideZones = new Set();
      this.hideObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) this.visibleHideZones.add(entry.target);
            else this.visibleHideZones.delete(entry.target);
          }
          this.update();
        },
        { threshold: 0 }
      );

      hideZones.forEach((zone) => this.hideObserver.observe(zone));
    }

    this.button?.addEventListener('click', this.scrollToTarget);
  }

  update() {
    const hiddenByZone = Boolean(this.visibleHideZones?.size);
    this.bar?.toggleAttribute('data-visible', Boolean(this.scrolledPast) && !this.footerReached && !hiddenByZone);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.footerObserver?.disconnect();
    this.hideObserver?.disconnect();
    this.button?.removeEventListener('click', this.scrollToTarget);

    // L'élément est remplacé à chaque réenregistrement dans l'éditeur de thème.
    // Sans ce retrait, chaque sauvegarde laisserait une barre orpheline sur
    // `body` et elles s'empileraient.
    this.bar?.remove();
  }

  scrollToTarget = () => {
    if (!this.target) return;

    const offset = parseInt(this.dataset.offset || '0', 10);
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const top = this.target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({ top, behavior: reduced ? 'auto' : 'smooth' });
  };
}

if (!customElements.get('sticky-cta')) customElements.define('sticky-cta', StickyCta);
